import React, { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';

const USER_ROLES = [
  'Loan Assistant',
  'Loan Officer', 
  'Loan Processor', 
  'Loan Underwriter',
  'Closer',
  'Post Closer',
  'Funder'
];

const ChannelUsersModal = ({ channel, onClose }) => {
  const [users, setUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRole, setSelectedRole] = useState(USER_ROLES[0]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch channel users and available users on mount
  useEffect(() => {
    // Combined fetch to ensure availability is calculated correctly
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      // Fetch channel details with users
      const channelResponse = await apiClient.get(`/channels/${channel.id}`);
      const channelUsers = channelResponse.data.users || [];
      setUsers(channelUsers);
  
      // Fetch all users
      const usersResponse = await apiClient.get('/users');
      const allUsers = usersResponse.data.users || []; // Change this line
  
      // Filter out users already in the channel
      const currentUserIds = new Set(channelUsers.map(u => u.id));
      const filteredUsers = allUsers.filter(user => !currentUserIds.has(user.id));
      setAvailableUsers(filteredUsers);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load channel users and available users');
    } finally {
      setIsLoading(false);
    }
  };
  const handleAddUser = async () => {
    if (!selectedUser) return;

    try {
      await apiClient.post(`/channels/${channel.id}/users`, {
        user_id: selectedUser,
        role: selectedRole
      });
      
      // Refresh data
      fetchData();
      
      // Reset selection
      setSelectedUser('');
      setSelectedRole(USER_ROLES[0]);
    } catch (error) {
      console.error('Error adding user to channel:', error);
      setError('Failed to add user to channel');
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await apiClient.delete(`/channels/${channel.id}/users/${userId}`);
      
      // Refresh data
      fetchData();
    } catch (error) {
      console.error('Error removing user from channel:', error);
      setError('Failed to remove user from channel');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] flex flex-col">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          Channel Users for {channel.channel_name}
        </h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Add User Section */}
        <div className="flex gap-4 mb-6">
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="flex-grow px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">Select a user to add</option>
            {availableUsers.map(user => (
              <option key={user.id} value={user.id}>
                {user.full_name} ({user.username})
              </option>
            ))}
          </select>

          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="w-40 px-3 py-2 border border-gray-300 rounded-md"
          >
            {USER_ROLES.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>

          <button
            onClick={handleAddUser}
            disabled={!selectedUser}
            className={`px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 ${
              !selectedUser ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Add User
          </button>
        </div>

        {/* Users List */}
        <div className="flex-grow overflow-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="4" className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-4 text-gray-500">
                    No users in this channel
                  </td>
                </tr>
              ) : (
                users.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.full_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.username}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.role === 'Loan Officer'
                          ? 'bg-blue-100 text-blue-800'
                          : user.role === 'Loan Underwriter'
                          ? 'bg-green-100 text-green-800'
                          : user.role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleRemoveUser(user.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Remove User"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Close Button */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChannelUsersModal;